"use client";

import React, { useEffect, useState } from "react";
import { Text } from "../../atoms/text/text";
import {
  PLAIN_LEGAL_FIELDS,
  extractLegalHtml,
  fetchLegalPayload,
  stripRefused
} from "../legal-modal/legal-content";
import { withSafeRender } from "../../atoms/safe-render";

export interface InPageLegalFetchProps {
  url: string;
  /** Shown in place of the copy when the document cannot be loaded. */
  unavailableText: string;
  id?: string;
  /** The block's own classes — this draws the `<aside>` in place of In-Page Legal. */
  className?: string;
  copyClassName?: string;
  /** The block's author colours, as In-Page Legal sets them on its own aside. */
  style?: React.CSSProperties;
  /**
   * The optional tooltip and "See details" link, drawn after the copy — and
   * after the unavailable wording too, as production draws its tooltip prop
   * whatever the fetch did.
   */
  children?: React.ReactNode;
}

/**
 * In-Page Legal with its copy fetched from a legal document in the browser —
 * the authored href has no host, so only a browser can resolve it (see
 * legal-content.ts).
 *
 * Production's outcomes (dds.ui.dtv-in-page-legal 5.0.74):
 * - the request fails: `unavailableText` replaces the copy, link kept;
 * - the document loads without `legalLong_text`: nothing at all;
 * - otherwise the copy.
 * Added here: the copy goes through stripRefused, and renders nothing if no
 * visible text survives it. Nothing renders while the document loads.
 */
function InPageLegalFetchImpl({
  url,
  unavailableText,
  id,
  className,
  copyClassName,
  style,
  children
}: InPageLegalFetchProps) {
  /* undefined while loading, null once the request has failed, "" for a
     document with nothing to show. */
  const [html, setHtml] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    /* Discards a response for a url this block no longer shows. */
    let live = true;
    setHtml(undefined);
    fetchLegalPayload(url).then(payload => {
      if (!live) {
        return;
      }
      if (payload === null) {
        console.warn(`[InPageLegal] could not load ${url}`);
        setHtml(null);
        return;
      }
      const field = extractLegalHtml(payload, PLAIN_LEGAL_FIELDS);
      if (field === null) {
        console.warn(`[InPageLegal] ${url} has no legalLong_text`);
      }
      setHtml(field === null ? "" : stripRefused(field));
    }).catch(() => {
      if (live) {
        setHtml(null);
      }
    });
    return () => {
      live = false;
    };
  }, [url]);

  if (html === undefined || html === "") {
    return null;
  }

  return (
    <aside className={className} id={id} style={style}>
      <Text as="p" variant="label" tone="muted" className={copyClassName}>
        {html ?? unavailableText}
      </Text>
      {children}
    </aside>
  );
}
export const InPageLegalFetch = withSafeRender(InPageLegalFetchImpl, "InPageLegalFetch");

export default InPageLegalFetch;
